// @flow
import React from 'react';
import styled from 'styled-components';
import {
  updateArrayItem,
  updateObject,
  arrToObject,
  removeArrayIndex,
  partial,
  stringify,
  isEmpty
} from 'utils/functional';
import { camelCaseToDash } from 'utils/string';
import Container from 'components/Container';
import Spinner from 'components/Spinner';
import Flex from 'components/Flex';
import Box from 'components/Box';
import Text, { BodySmall, H4, H5, HeadingSmall } from 'components/Text';
import Divider from 'components/Divider';
import { SearchableSelect } from 'components/Select';
import LoadingSection from 'components/LoadingSection';
import { fetchSubsites, fetchGradFairs } from 'api/cms';
import { fetchScheduledContentQueues } from 'api/scheduledContent';
import type {
  PersonaAnswer,
  PersonaQuestion,
  PersonaResult,
  FourArticlesHighlightBlockData,
  CategoryHighlightData,
  TileGridSection
} from 'api/cms';
import CategoriesContainer from 'containers/CategoriesContainer';
import Input from 'components/Input';
import Checkbox from 'components/Checkbox';
import {
  AutocompleteTextInputField,
  DualAutocompleteTextInputField,
  AutocompleteSimpleInputField,
  CheckboxField,
  RichTextField,
  ImagePickerField,
  InputField,
  RadioButtonsField,
  MultiInputGroup,
  SelectField,
  TextAreaField,
  MultiPillField,
  Field,
  FormContextConsumer,
  GalleryPickerField,
  HiddenField,
  Label,
  Description,
  ButtonLinkGroup,
  SearchableSelectField
} from 'components/Form';
import {
  Field as FormBuilderField,
  FormContainer as FormBuilderFormContainer
} from 'components/Cms/FormBuilderBlock';
import {
  Question as PersonaQuestionPreview,
  Result as PersonaResultPreview
} from 'components/Cms/PersonaQuizBlock';
import Button from 'components/Button';
import Icon from 'components/Icon';
import { ErrorContainer } from 'components/Form/Field';
import { IDEALIST_DAY_EVENT_DATES } from 'utils/constants';
import EmailBgImage from 'blue-loops.svg';
import ErrorIcon from 'error.svg';
import { colors, transition, breakpoints } from '../../theme';

const { useState, useEffect, useCallback, useMemo } = React;

const FreeCodeTextArea = styled(TextAreaField)`
  font-family: Monaco Menlo, Consolas, 'Courier New', monospace;
`;

const PreviewPane = styled(Box)`
  position: fixed;
  z-index: 1001;
  top: 50%;
  left: 50%;
  width: 80%;
  transform: translate(-50%, -50%) scale(0.6);
  padding: 40px;
  border-radius: 24px;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  opacity: 0;
  visibility: hidden;
  transition: all ${transition};
  pointer-events: none;
  * {
    white-space: normal;
  }
  @media (min-width: ${breakpoints[2]}) {
    width: 960px;
  }
`;

const PreviewButton = styled(Button)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  background: ${colors.aluminum};
  border-radius: 50%;
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1000;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.3);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: all ${transition};
  }
  &:hover,
  &:focus {
    & + ${/* sc-selector */ PreviewPane}, &::before {
      visibility: visible;
      opacity: 1;
    }
  }
`;

export const RichtextBlock = ({
  forSidebars,
  inputId,
  name,
  'data-qa-id': qaId,
  index,
  placeholder
}: {
  forSidebars?: boolean,
  inputId: Function,
  name: string,
  index: number,
  placeholder: string,
  'data-qa-id': string
}) => (
  <RichTextField
    id={inputId}
    label={`${getText('Rich Text')}*`}
    name={`${name}.${index}.data.html`}
    data-qa-id={`${qaId}-html`}
    placeholder={placeholder}
    limited={forSidebars}
  />
);

export const ImageBlock = ({
  inputId,
  name,
  'data-qa-id': qaId,
  index
}: {
  inputId: Function,
  name: string,
  index: number,
  hasError: string,
  'data-qa-id': string
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <InputField
      name={`${name}.${index}.data.headline`}
      label={`${getText('Headline')}`}
    />
    <RichTextField
      simple
      label={`${getText('Subheadline')}`}
      name={`${name}.${index}.data.subtitle`}
    />
    <FormContextConsumer>
      {({ updateValueWith }) => {
        const update = partial(updateValueWith, `${name}.${index}.data.image`);
        return (
          <ImagePickerField
            id={inputId}
            data-qa-id={`${qaId}-image`}
            label={`${getText('Image')}*`}
            name={`${name}.${index}.data.image`}
            previewWidth={300}
            previewHeight={300}
            hideRemove
            onChange={newImage => update(v => updateObject(v, newImage))}
            disableCrop
          />
        );
      }}
    </FormContextConsumer>
    <Container mt="20px">
      <FormContextConsumer>
        {({ updateValueWith }) => {
          const update = partial(
            updateValueWith,
            `${name}.${index}.data.image`
          );
          return (
            <InputField
              name={`${name}.${index}.data.image.altText`}
              label={getText('Alt Text')}
              data-qa-id={`${qaId}-image-altText`}
              onChange={({ target: { value: altText } }) =>
                update(v => updateObject(v, { altText }))
              }
            />
          );
        }}
      </FormContextConsumer>
      <FormContextConsumer>
        {({ updateValueWith }) => {
          const update = partial(
            updateValueWith,
            `${name}.${index}.data.image`
          );
          return (
            <InputField
              name={`${name}.${index}.data.image.caption`}
              data-qa-id={`${qaId}-image-caption`}
              label={getText('Caption')}
              maxLength="1000"
              onChange={({ target: { value: caption } }) =>
                update(v => updateObject(v, { caption }))
              }
            />
          );
        }}
      </FormContextConsumer>
      <HiddenField
        name={`${name}.${index}.data.width`}
        data-qa-id={`${qaId}-width`}
      />
      <InputField
        name={`${name}.${index}.data.url`}
        label={getText('URL')}
        description={getText('Optional URL to link to image')}
        data-qa-id={`${qaId}-url`}
      />
    </Container>
  </Flex>
);

export const BlockquoteBlock = ({
  inputId,
  name,
  'data-qa-id': qaId,
  index,
  placeholder
}: {
  inputId: Function,
  name: string,
  index: number,
  hasError: string,
  placeholder: string,
  'data-qa-id': string
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <RichTextField
      simple
      id={inputId}
      label={`${getText('Quote')}*`}
      name={`${name}.${index}.data.quote`}
      data-qa-id={`${qaId}-quote`}
      placeholder={placeholder || ''}
    />
    <InputField
      name={`${name}.${index}.data.cite`}
      label={getText('Citation')}
      data-qa-id={`${qaId}-blockquote-cite`}
    />
  </Flex>
);

export const GradCtaHeroBlock = ({
  inputId,
  name,
  'data-qa-id': qaId,
  index
}: {
  inputId: Function,
  name: string,
  index: number,
  hasError: string,
  placeholder: string,
  'data-qa-id': string
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <ImagePickerField
      id={inputId}
      data-qa-id={`${qaId}-image`}
      label={`${getText('Image')}*`}
      name={`${name}.${index}.data.image`}
      previewWidth={400}
      previewHeight={150}
      previewCrop
      hideRemove
    />
    <Container mt="20px">
      <InputField
        name={`${name}.${index}.data.text`}
        label={`${getText('Headline')}*`}
        data-qa-id={`${qaId}-text`}
      />
    </Container>
  </Flex>
);

export const CtaHeroBlock = ({
  inputId,
  name,
  'data-qa-id': qaId,
  index
}: {
  inputId: Function,
  name: string,
  index: number,
  hasError: string,
  placeholder: string,
  'data-qa-id': string
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <ImagePickerField
      id={inputId}
      data-qa-id={`${qaId}-image`}
      label={`${getText('Image')}*`}
      name={`${name}.${index}.data.image`}
      previewWidth={400}
      previewHeight={150}
      previewCrop
      hideRemove
    />
    <Container mt="20px">
      <InputField
        name={`${name}.${index}.data.text`}
        label={`${getText('Headline')}*`}
        data-qa-id={`${qaId}-text`}
      />
      <InputField
        name={`${name}.${index}.data.buttonText`}
        label={`${getText('Button Text')}`}
        data-qa-id={`${qaId}-button-text`}
      />
      <InputField
        name={`${name}.${index}.data.buttonUrl`}
        label={`${getText('Button Url')}`}
        data-qa-id={`${qaId}-button-url`}
      />
    </Container>
  </Flex>
);

const getAnchors = values =>
  values.contentBlocks
    .filter(block => block.type === 'ANCHOR')
    .map(block => block.data.anchor || '');

export const StickyHeaderLinksGradBlock = ({
  name,
  'data-qa-id': qaId,
  index
}: {
  name: string,
  index: number,
  'data-qa-id': string
}) => (
  <FormContextConsumer>
    {({ values }) => {
      const options = getAnchors(values);
      return (
        <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
          <MultiInputGroup
            title={getText('Sticky Links Hero')}
            name={`${name}.${index}.data.links`}
            minInputGroups={1}
            data-qa-id={`${qaId}-links`}
            maxInputGroups={4}
            renderInputGroup={namePrefix => (
              <>
                <InputField
                  name={`${namePrefix}heading`}
                  label={`${getText('Heading')}*`}
                />

                <InputField
                  name={`${namePrefix}subHeading`}
                  label={getText('Subheading')}
                />

                <AutocompleteSimpleInputField
                  name={`${namePrefix}url`}
                  label={`${getText('Anchor URL Slug')}*`}
                  options={options}
                  shouldRenderSuggestions={() => true}
                />
              </>
            )}
          />
        </Flex>
      );
    }}
  </FormContextConsumer>
);

const FlashCard = ({ namePrefix }: { namePrefix: string }) => (
  <>
    <TextAreaField
      name={`${namePrefix}instructions`}
      rows="5"
      maxLength={500}
      label={getText('Instructions')}
      description={getText('Enter instructions here')}
    />
    <ImagePickerField
      label={`${getText('Image')}`}
      name={`${namePrefix}image`}
      previewWidth={180}
      previewHeight={120}
      disableCrop
    />
    <RichTextField
      name={`${namePrefix}question`}
      label={getText('Question')}
      simple
    />
    <RichTextField
      name={`${namePrefix}answer`}
      label={getText('Answer')}
      simple
    />
  </>
);
export const FlashCardQuizBlock = ({
  name,
  'data-qa-id': qaId,
  index
}: {
  name: string,
  index: number,
  'data-qa-id': string
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <MultiInputGroup
      title={getText('Flash Cards')}
      name={`${name}.${index}.data.cards`}
      minInputGroups={1}
      data-qa-id={`${qaId}-cards`}
      addText={
        /* TRANSLATORS: Text to add flash card question in CMS */ getText(
          '+ Add Question'
        )
      }
      renderInputGroup={namePrefix => <FlashCard namePrefix={namePrefix} />}
    />
  </Flex>
);

const PersonaAnswerBlock = ({
  fieldQaIdPrefix,
  namePrefix,
  updateValue,
  results,
  points,
  errors
}: {
  fieldQaIdPrefix: string,
  namePrefix: string,
  updateValue: Function,
  results: Array<PersonaResult>,
  points: Array<number>,
  errors: APIErrors
}) => {
  const name = `${namePrefix}points`;
  const fieldError = errors[name];

  return (
    <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
      <InputField
        name={`${namePrefix}title`}
        label={getText('Answer')}
        description={getText('Enter answer here')}
      />
      <H5 mb="8px">Result Scores</H5>
      <BodySmall>Assign 0–2 points to each</BodySmall>
      {results && (
        <Box mt={12}>
          {fieldError && (
            <ErrorContainer>
              <ErrorIcon />
              <Text pl="4px">{fieldError}</Text>
            </ErrorContainer>
          )}
          {results.map((result, pointIndex) => {
            const qaId = `${fieldQaIdPrefix}-${camelCaseToDash(
              namePrefix
            ).replace(/\./g, '-')}point-${pointIndex}`;
            return (
              <Flex
                key={result.title || result.tempId}
                alignItems="center"
                mt="4px"
                className={fieldError ? 'error' : null} // used to scroll to error
              >
                <Box width={72} flex="0 0 auto" mr={20}>
                  <Input
                    data-qa-id={qaId}
                    defaultValue={points ? points[pointIndex] : ''}
                    min="0"
                    max="2"
                    type="number"
                    label={getText('Result Score')}
                    hasError={fieldError}
                    onChange={({ target: { value: point } }) => {
                      const newPoints = updateArrayItem(
                        points,
                        pointIndex,
                        Number(point)
                      );
                      updateValue(v => updateObject(v, { points: newPoints }));
                      return point;
                    }}
                  />
                </Box>
                <BodySmall>
                  {result.title || `Result ${pointIndex + 1} (needs title)`}
                </BodySmall>
              </Flex>
            );
          })}
        </Box>
      )}
    </Flex>
  );
};

const PersonaQuestionBlock = ({
  formPrefix,
  namePrefix
}: {
  formPrefix: string,
  namePrefix: string,
  index: number
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <InputField
      name={`${namePrefix}title`}
      label={getText('Question')}
      description={getText('Enter question here')}
    />
    <ImagePickerField
      label={`${getText('Image*')}`}
      name={`${namePrefix}image`}
      previewWidth={180}
      previewHeight={120}
      disableCrop
    />
    <Box p={24} borderRadius="8px" bg={colors.fog}>
      <Label>{getText('Answers')}</Label>
      <FormContextConsumer>
        {({ fieldQaIdPrefix, getValue, errors }) => (
          <MultiInputGroup
            title={getText('Answers')}
            name={`${namePrefix}answers`}
            defaultData={[{ title: '', points: [] }]}
            minInputGroups={1}
            data-qa-id={`${namePrefix.replace(/\./g, '-')}`}
            addText={
              /* TRANSLATORS: Text to add flash card answer to question in CMS */ getText(
                '+ Add Answer'
              )
            }
            errors={errors}
            getValue={getValue}
            collapsible
            renderInputGroup={(
              answerNamePrefix,
              answerSection,
              updateValue,
              answerIndex
            ) => {
              const answer = ((getValue(
                `${namePrefix}answers.${answerIndex}`
              ): any): PersonaAnswer);
              const results = ((getValue(
                `${formPrefix}results`
              ): any): Array<PersonaResult>);

              return (
                <PersonaAnswerBlock
                  fieldQaIdPrefix={fieldQaIdPrefix || ''}
                  namePrefix={answerNamePrefix}
                  updateValue={updateValue}
                  points={answer.points}
                  results={results}
                  errors={errors}
                />
              );
            }}
            renderTitle={(answerNamePrefix, answerSection, answerIndex) => {
              const title = ((getValue(
                `${namePrefix}answers.${answerIndex}.title`
              ): any): string);
              return `Answer ${answerIndex + 1}: ${title}`;
            }}
          />
        )}
      </FormContextConsumer>
    </Box>
  </Flex>
);

const PersonaResultBlock = ({ namePrefix }: { namePrefix: string }) => (
  <>
    <InputField
      name={`${namePrefix}title`}
      label={getText('Title')}
      description={getText('Enter result title here')}
    />
    <ImagePickerField
      label={`${getText('Image*')}`}
      name={`${namePrefix}image`}
      previewWidth={180}
      previewHeight={120}
      disableCrop
    />
    <RichTextField
      name={`${namePrefix}description`}
      description={getText('Enter result description here')}
      label={getText('Description')}
      simple
    />
    <InputField
      name={`${namePrefix}socialMediaText`}
      rows="3"
      maxLength="80"
      label={getText('Social Media Text')}
      description={getText('Enter social share text here')}
    />
  </>
);

export const PersonaQuizBlock = ({
  name,
  index,
  qaId
}: {
  name: string,
  index: number,
  qaId: string
}) => (
  <FormContextConsumer>
    {({ getValue, errors }) => (
      <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
        <MultiInputGroup
          title={getText('Results')}
          name={`${name}.${index}.data.results`}
          minInputGroups={1}
          data-qa-id={`${qaId}-results`}
          getValue={getValue}
          addText={
            /* TRANSLATORS: Text to add persona quiz result in CMS */ getText(
              '+ Add Result'
            )
          }
          renderInputGroup={(namePrefix, section, resultsIndex) => (
            <PersonaResultBlock namePrefix={namePrefix} index={resultsIndex} />
          )}
          renderTitle={(resultNamePrefix, resultSection, resultIndex) => {
            const result = ((getValue(
              `${name}.${index}.data.results.${resultIndex}`
            ): any): PersonaResult);
            const title = result.title;
            return (
              <Box display="flex" alignItems="center">
                <PreviewButton>
                  <Box>
                    <Icon
                      name="preview"
                      display="block"
                      width={20}
                      height={20}
                      stroke="currentColor"
                    />
                  </Box>
                </PreviewButton>
                <PreviewPane>
                  <PersonaResultPreview index={0} result={result} />
                </PreviewPane>
                <Box textOverflow="ellipsis" overflow="hidden" ml={12}>
                  {`Result ${resultIndex + 1}: `}
                  <Box as="span" fontWeight="normal">
                    {title}
                  </Box>
                </Box>
              </Box>
            );
          }}
          errors={errors}
          collapsible
        />
        <Divider my={48} />
        <MultiInputGroup
          title={getText('Questions')}
          name={`${name}.${index}.data.questions`}
          minInputGroups={1}
          data-qa-id={`${qaId}-questions`}
          defaultData={[{ title: '', answers: [{ title: '', points: [] }] }]}
          addText={
            /* TRANSLATORS: Text to add persona quiz question in CMS */ getText(
              '+ Add Question'
            )
          }
          getValue={getValue}
          renderInputGroup={(
            questionPrefix,
            section,
            updateValue,
            questionsIndex
          ) => (
            <PersonaQuestionBlock
              formPrefix={`${name}.${index}.data.`}
              namePrefix={questionPrefix}
              index={questionsIndex}
            />
          )}
          renderTitle={(questionNamePrefix, questionSection, questionIndex) => {
            const question = ((getValue(
              `${name}.${index}.data.questions.${questionIndex}`
            ): any): PersonaQuestion);
            const title = question.title;
            return (
              <Box display="flex" alignItems="center">
                <PreviewButton>
                  <Box>
                    <Icon
                      name="preview"
                      display="block"
                      width={20}
                      height={20}
                      stroke="currentColor"
                    />
                  </Box>
                </PreviewButton>
                <PreviewPane>
                  <PersonaQuestionPreview index={0} question={question} />
                </PreviewPane>
                <Box textOverflow="ellipsis" overflow="hidden" ml={12}>
                  {`Question ${questionIndex + 1}: `}
                  <Box as="span" fontWeight="normal">
                    {title}
                  </Box>
                </Box>
              </Box>
            );
          }}
          errors={errors}
          collapsible
        />
      </Flex>
    )}
  </FormContextConsumer>
);

export const StickyHeaderLinksBlock = ({
  name,
  'data-qa-id': qaId,
  index
}: {
  inputId: Function,
  name: string,
  index: number,
  hasError: string,
  placeholder: string,
  'data-qa-id': string
}) => (
  <FormContextConsumer>
    {({ values }) => {
      const options = getAnchors(values);
      return (
        <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
          <InputField
            name={`${name}.${index}.data.buttonText`}
            label={`${getText('CTA Button Text')}`}
          />
          <InputField
            name={`${name}.${index}.data.buttonUrl`}
            label={`${getText('CTA Button URL')}`}
          />
          <MultiInputGroup
            title={getText('Sticky Links Hero')}
            name={`${name}.${index}.data.links`}
            minInputGroups={1}
            data-qa-id={`${qaId}-links`}
            maxInputGroups={4}
            renderInputGroup={namePrefix => (
              <>
                <InputField
                  name={`${namePrefix}heading`}
                  label={`${getText('Heading')}*`}
                />
                <InputField
                  name={`${namePrefix}subHeading`}
                  label={getText('Subheading')}
                />
                <AutocompleteSimpleInputField
                  name={`${namePrefix}url`}
                  label={`${getText('Anchor URL Slug')}*`}
                  options={options}
                  shouldRenderSuggestions={() => true}
                />
              </>
            )}
          />
        </Flex>
      );
    }}
  </FormContextConsumer>
);

export const AccordionBlock = ({
  index,
  name,
  'data-qa-id': qaId
}: {
  index: number,
  name: string,
  'data-qa-id': string
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <SelectField
      name={`${name}.${index}.data.default`}
      label={getText('Default Behavior')}
      options={[
        { value: 'ALL_CLOSED', label: 'All Closed' },
        { value: 'FIRST_ONE_OPENED', label: 'First One Opened' }
      ]}
    />
    <InputField
      name={`${name}.${index}.data.headline`}
      label={`${getText('Headline')}`}
    />
    <RichTextField
      name={`${name}.${index}.data.subtitle`}
      label={`${getText('Subheadline')}`}
      simple
    />
    <Divider dashed my="48px" />
    <MultiInputGroup
      title={getText('Accordion')}
      name={`${name}.${index}.data.sections`}
      minInputGroups={1}
      data-qa-id={`${qaId}-${index}-sections`}
      confirmationText={getText(
        'Are you sure you want to remove this accordion section?'
      )}
      renderInputGroup={namePrefix => (
        <>
          <InputField
            name={`${namePrefix}title`}
            label={`${getText('Section Title')}*`}
          />

          <RichTextField
            simple
            name={`${namePrefix}content`}
            label={`${getText('Section Content')}*`}
          />
        </>
      )}
    />
  </Flex>
);

export const AnchorBlock = ({
  index,
  name,
  'data-qa-id': qaId
}: {
  index: number,
  name: string,
  'data-qa-id': string
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <InputField
      name={`${name}.${index}.data.anchor`}
      label={`${getText('Anchor Slug')}*`}
      data-qa-id={`${qaId}-anchor`}
    />
  </Flex>
);

export const ThreeStepCtaBlock = ({
  index,
  name,
  'data-qa-id': qaId
}: {
  index: number,
  name: string,
  'data-qa-id': string
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <InputField
      name={`${name}.${index}.data.mainHeadline`}
      label={`${getText('Main Headline')}`}
      data-qa-id={`${qaId}-main-headline`}
    />
    <RichTextField
      simple
      name={`${name}.${index}.data.mainText`}
      label={`${getText('Main Text')}`}
      data-qa-id={`${qaId}-main-text`}
    />
    <InputField
      name={`${name}.${index}.data.buttonText`}
      label={getText('CTA Button Text')}
      data-qa-id={`${qaId}-button-text`}
    />
    <InputField
      name={`${name}.${index}.data.buttonUrl`}
      label={getText('CTA Button URL')}
      data-qa-id={`${qaId}-button-url`}
    />
    <SelectField
      name={`${name}.${index}.data.imageSize`}
      data-qa-id={`${qaId}-data-image-size`}
      label={getText('Image Size')}
      options={[
        { value: 'SMALL', label: 'Small' },
        { value: 'LARGE', label: 'Large' }
      ]}
    />
    {['One', 'Two', 'Three'].map(step => {
      const lcStep = step.toLowerCase();
      return (
        <Box key={step}>
          <H4>Step {step}</H4>
          <ImagePickerField
            data-qa-id={`${qaId}-step-${lcStep}-image`}
            label={getText('Image')}
            name={`${name}.${index}.data.step${step}Image`}
            previewWidth={200}
            previewHeight={150}
            disableCrop
          />
          <InputField
            name={`${name}.${index}.data.step${step}Headline`}
            label={`${getText('Headline')}*`}
            data-qa-id={`${qaId}-step-${lcStep}-headline`}
          />
          <RichTextField
            simple
            name={`${name}.${index}.data.step${step}Text`}
            label={`${getText('Text')}*`}
            data-qa-id={`${qaId}-step-${lcStep}-text`}
          />
          <InputField
            name={`${name}.${index}.data.step${step}Url`}
            label={`${getText('Url')}`}
            data-qa-id={`${qaId}-step-${lcStep}-url`}
          />
        </Box>
      );
    })}
  </Flex>
);

export const TwoTilesBlock = ({
  index,
  name,
  'data-qa-id': qaId
}: {
  index: number,
  name: string,
  'data-qa-id': string
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <H4 pb="10px">Tile One</H4>
    <InputField
      name={`${name}.${index}.data.tileOneHeadline`}
      label={`${getText('Headline')}*`}
      data-qa-id={`${qaId}-tile-one-headline`}
    />
    <RichTextField
      simple
      name={`${name}.${index}.data.tileOneText`}
      label={`${getText('Text')}*`}
      data-qa-id={`${qaId}-tile-one-text`}
    />
    <InputField
      name={`${name}.${index}.data.tileOneButtonText`}
      label={getText('Button Text')}
      data-qa-id={`${qaId}-tile-one-button-text`}
    />
    <InputField
      name={`${name}.${index}.data.tileOneButtonUrl`}
      label={getText('Button URL')}
      data-qa-id={`${qaId}-tile-one-button-url`}
    />
    <H4 pb="10px">Tile Two</H4>
    <InputField
      name={`${name}.${index}.data.tileTwoHeadline`}
      label={`${getText('Headline')}*`}
      data-qa-id={`${qaId}-tile-two-headline`}
    />
    <RichTextField
      simple
      name={`${name}.${index}.data.tileTwoText`}
      label={`${getText('Text')}*`}
      data-qa-id={`${qaId}-tile-two-text`}
    />
    <InputField
      name={`${name}.${index}.data.tileTwoButtonText`}
      label={getText('Button Text')}
      data-qa-id={`${qaId}-tile-two-button-text`}
    />
    <InputField
      name={`${name}.${index}.data.tileTwoButtonUrl`}
      label={getText('Button URL')}
      data-qa-id={`${qaId}-tile-two-button-url`}
    />
  </Flex>
);

export const VideoBlock = ({
  name,
  'data-qa-id': qaId,
  index
}: {
  inputId: Function,
  name: string,
  index: number,
  hasError: string,
  placeholder: string,
  'data-qa-id': string
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <InputField
      name={`${name}.${index}.data.headline`}
      label={`${getText('Headline')}`}
      description={getText('Headline that appears above the video')}
      data-qa-id={`${qaId}-headline`}
    />
    <RichTextField
      simple
      label={`${getText('Subheadline')}`}
      name={`${name}.${index}.data.subtitle`}
    />
    <InputField
      name={`${name}.${index}.data.videoUrl`}
      label={`${getText('Video URL')}*`}
      placeholder={getText(
        'e.g. https://youtu.be/ktKVQfNQXQw or https://vimeo.com/49671279'
      )}
      data-qa-id={`${qaId}-video-url`}
    />
    <HiddenField
      name={`${name}.${index}.data.width`}
      data-qa-id={`${qaId}-width`}
    />
  </Flex>
);

export const FreeCodeBlock = ({
  name,
  'data-qa-id': qaId,
  index
}: {
  inputId: Function,
  name: string,
  index: number,
  hasError: string,
  placeholder: string,
  'data-qa-id': string
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <FreeCodeTextArea
      name={`${name}.${index}.data.code`}
      rows="5"
      label={`${getText('Code / HTML')}*`}
      data-qa-id={`${qaId}-free-code`}
    />
    <FormContextConsumer>
      {({ getValue, updateValue }) => (
        <Checkbox
          checked={!getValue(`${name}.${index}.data.isVisible`)}
          label={`${getText('Hide this content')}`}
          onChange={e => updateValue(`${name}.${index}.data.isVisible`, !e)}
        />
      )}
    </FormContextConsumer>
  </Flex>
);

export const EmailCampaignSignupSingleBlock = ({
  name,
  'data-qa-id': qaId,
  index
}: {
  inputId: Function,
  name: string,
  index: number,
  hasError: string,
  placeholder: string,
  'data-qa-id': string
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <InputField
      name={`${name}.${index}.data.headline`}
      label={`${getText('Headline')}*`}
      data-qa-id={`${qaId}-headline`}
    />
    <InputField
      name={`${name}.${index}.data.buttonText`}
      label={`${getText('Button Text')}*`}
      data-qa-id={`${qaId}-button-text`}
    />
    <InputField
      name={`${name}.${index}.data.campaignId`}
      label={`${getText('Campaign ID')}*`}
      data-qa-id={`${qaId}-campaign-id`}
    />
    <InputField
      name={`${name}.${index}.data.listId`}
      label={`${getText('List ID')}*`}
      data-qa-id={`${qaId}-list-id`}
    />
    <ImagePickerField
      name={`${name}.${index}.data.bgImage`}
      label={`${getText('Background Image')}`}
      previewWidth={416}
      previewHeight={170}
      previewCrop
      defaultImage={EmailBgImage}
    />
  </Flex>
);

export const EmailCampaignSignupDoubleBlock = ({
  name,
  'data-qa-id': qaId,
  index
}: {
  inputId: Function,
  name: string,
  index: number,
  hasError: string,
  placeholder: string,
  'data-qa-id': string
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <InputField
      name={`${name}.${index}.data.headline`}
      label={`${getText('Headline')}*`}
      data-qa-id={`${qaId}-headline`}
    />
    <InputField
      name={`${name}.${index}.data.toggleTextOne`}
      label={`${getText('Toggle Text One')}*`}
      data-qa-id={`${qaId}-toggle-text-one`}
    />
    <InputField
      name={`${name}.${index}.data.buttonTextOne`}
      label={`${getText('Button Text One')}*`}
      data-qa-id={`${qaId}-button-text-one`}
    />
    <InputField
      name={`${name}.${index}.data.campaignIdOne`}
      label={`${getText('Campaign ID One')}*`}
      data-qa-id={`${qaId}-campaign-id-one`}
    />
    <InputField
      name={`${name}.${index}.data.listIdOne`}
      label={`${getText('List ID One')}*`}
      data-qa-id={`${qaId}-list-id-one`}
    />
    <InputField
      name={`${name}.${index}.data.toggleTextTwo`}
      label={`${getText('Toggle Text Two')}*`}
      data-qa-id={`${qaId}-toggle-text-two`}
    />
    <InputField
      name={`${name}.${index}.data.buttonTextTwo`}
      label={`${getText('Button Text Two')}*`}
      data-qa-id={`${qaId}-button-text-two`}
    />
    <InputField
      name={`${name}.${index}.data.campaignIdTwo`}
      label={`${getText('Campaign ID Two')}*`}
      data-qa-id={`${qaId}-campaign-id-two`}
    />
    <InputField
      name={`${name}.${index}.data.listIdTwo`}
      label={`${getText('List ID Two')}*`}
      data-qa-id={`${qaId}-list-id-two`}
    />
    <ImagePickerField
      name={`${name}.${index}.data.bgImage`}
      label={`${getText('Background Image')}`}
      previewWidth={416}
      previewHeight={170}
      previewCrop
      defaultImage={EmailBgImage}
    />
  </Flex>
);

export const FormBuilderBlock = ({
  name,
  'data-qa-id': qaId,
  index
}: {
  name: string,
  'data-qa-id': string,
  index: number
}) => {
  const namePrefix = `${name}.${index}.`;
  return (
    <FormContextConsumer>
      {({ getValue }) => (
        <>
          <InputField
            name={`${namePrefix}data.headline`}
            label={getText('Headline')}
          />
          <InputField
            name={`${namePrefix}data.action`}
            description={getText(
              'The URL the form will submit to, e.g. http://pi.idealist.org/12345'
            )}
            label={`${getText('Form Action')}*`}
          />
          <SelectField
            name={`${namePrefix}data.method`}
            label={`${getText('Form Method')}`}
            options={[
              { value: 'POST', label: 'POST' },
              { value: 'GET', label: 'GET' }
            ]}
          />
          <InputField
            name={`${namePrefix}data.submitButtonText`}
            label={`${getText('Submit Button Text')}*`}
          />
          <ImagePickerField
            name={`${namePrefix}data.bgImage`}
            label={`${getText('Background Image')}`}
            previewWidth={416}
            previewHeight={170}
            previewCrop
            defaultImage={EmailBgImage}
          />

          <Label>{`${getText('Fields')}*`}</Label>
          <Box p={24} mb={24} borderRadius="8px" bg={colors.fog}>
            <MultiInputGroup
              title={getText('Fields')}
              name={`${namePrefix}data.fields`}
              defaultData={[
                {
                  type: 'TEXT',
                  options: [
                    {
                      label: ''
                    }
                  ]
                }
              ]}
              minInputGroups={1}
              data-qa-id={`${qaId}-${index}-fields`}
              collapsible
              addText={getText('+ Add Field')}
              renderTitle={(fieldNamePrefix, fieldSection, fieldIndex) => {
                const fieldLabel = ((getValue(
                  `${fieldNamePrefix}label`
                ): any): string);
                const fieldName = ((getValue(
                  `${fieldNamePrefix}name`
                ): any): string);
                return `Field ${fieldIndex + 1}${
                  fieldLabel
                    ? `: ${fieldLabel}`
                    : fieldName
                    ? `: ${fieldName}`
                    : ''
                }`;
              }}
              renderInputGroup={(fieldNamePrefix, data) => (
                <>
                  <SelectField
                    name={`${fieldNamePrefix}type`}
                    label={`${getText('Type')}`}
                    options={[
                      { value: 'TEXT', label: 'Text' },
                      { value: 'EMAIL', label: 'Email' },
                      { value: 'SELECT', label: 'Select' },
                      { value: 'CHECKBOX', label: 'Checkbox' },
                      { value: 'HIDDEN', label: 'Hidden' }
                    ]}
                  />
                  {data.type && data.type !== 'HIDDEN' && (
                    <>
                      <Label>{getText('Preview')}</Label>
                      <Box
                        borderRadius="8px"
                        p={16}
                        mb={30}
                        bg={colors.waterBlue}
                      >
                        <Box opacity={0.75}>
                          {data.type !== 'CHECKBOX' && (
                            <Box component={Label} color="white">
                              {data.label || (
                                <Box fontStyle="italic">{getText('Label')}</Box>
                              )}
                            </Box>
                          )}
                          <Box mt="0 !important">
                            <FormBuilderFormContainer
                              $disabled={['TEXT', 'EMAIL'].includes(data.type)}
                            >
                              <FormBuilderField
                                {...data}
                                required={false}
                                name={null}
                              />
                            </FormBuilderFormContainer>
                          </Box>
                        </Box>
                      </Box>
                    </>
                  )}
                  {data.type !== 'HIDDEN' && (
                    <InputField
                      name={`${fieldNamePrefix}label`}
                      description={
                        /* TRANSLATORS: Form Builder CMS Block */ getText(
                          'This is displayed to the user, and will not be submitted with the form'
                        )
                      }
                      label={`${getText('Label')}*`}
                    />
                  )}
                  <InputField
                    name={`${fieldNamePrefix}name`}
                    description={
                      /* TRANSLATORS: Form Builder CMS Block */ getText(
                        'This is submitted rather than shown to the user; if not provided to you (e.g. by Pardot), make something up using only lower-case letters and underscores'
                      )
                    }
                    label={`${getText('Name')}*`}
                  />
                  {data.type !== 'HIDDEN' && (
                    <CheckboxField
                      large
                      name={`${fieldNamePrefix}required`}
                      label={`${getText('Required')}`}
                    />
                  )}
                  {!['CHECKBOX', 'HIDDEN'].includes(data.type) && (
                    <InputField
                      name={`${fieldNamePrefix}placeholder`}
                      label={`${getText('Placeholder')}`}
                    />
                  )}
                  {['CHECKBOX', 'HIDDEN'].includes(data.type) && (
                    <InputField
                      name={`${fieldNamePrefix}value`}
                      label={`${getText('Value')}${
                        data.type === 'HIDDEN' ? '*' : ''
                      }`}
                      description={
                        data.type === 'CHECKBOX'
                          ? /* TRANSLATORS: Form Builder CMS Block */ getText(
                              'The value that gets submitted when the checkbox is checked'
                            )
                          : undefined
                      }
                    />
                  )}
                  {data.type === 'SELECT' && (
                    <Box mb={30}>
                      <Label>{getText('Options')}</Label>
                      <Description>
                        {/* TRANSLATORS: Form Builder CMS Block */ getText(
                          'Only the label is required. If left blank, the value will be the same as the label (what the user sees).'
                        )}
                      </Description>
                      <Box p={24} borderRadius="8px" bg="white">
                        <MultiInputGroup
                          title={getText('Options')}
                          name={`${fieldNamePrefix}options`}
                          defaultData={[
                            {
                              label: ''
                            }
                          ]}
                          minInputGroups={1}
                          data-qa-id={`${fieldNamePrefix.replace(/\./g, '-')}`}
                          addText={
                            /* TRANSLATORS: Text to add flash card tile to question in CMS */ getText(
                              '+ Add Option'
                            )
                          }
                          // errors={errors}
                          renderInputGroup={optionPrefix => (
                            <Box m="-4px" display={['block', 'flex']}>
                              <Box flex="0 0 auto" width={[1, 1 / 2]} p="4px">
                                <InputField
                                  name={`${optionPrefix}label`}
                                  label={`${getText('Label')}*`}
                                />
                              </Box>
                              <Box flex="0 0 auto" width={[1, 1 / 2]} p="4px">
                                <InputField
                                  name={`${optionPrefix}value`}
                                  label={getText('Value')}
                                />
                              </Box>
                            </Box>
                          )}
                          renderTitle={(
                            optionPrefix,
                            optionSection,
                            optionIndex
                          ) => `Option ${optionIndex + 1}`}
                        />
                      </Box>
                    </Box>
                  )}
                </>
              )}
            />
          </Box>
        </>
      )}
    </FormContextConsumer>
  );
};

export const ThreeSectionSliderBlock = ({
  name,
  'data-qa-id': qaId,
  index
}: {
  inputId: Function,
  name: string,
  index: number,
  hasError: string,
  placeholder: string,
  'data-qa-id': string
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <InputField
      name={`${name}.${index}.data.headline`}
      label={`${getText('Headline')}`}
      data-qa-id={`${qaId}-headline`}
    />
    <SelectField
      name={`${name}.${index}.data.imageSize`}
      label={getText('Image Size')}
      description={getText(
        'The size of all slider images, either small (height is 80px) or medium (height is 130px)'
      )}
      options={[
        { value: 'SMALL', label: 'Small' },
        { value: 'MEDIUM', label: 'Medium' }
      ]}
    />
    <Divider dashed my="48px" />
    <MultiInputGroup
      title={getText('Three Section Slider')}
      name={`${name}.${index}.data.sections`}
      minInputGroups={1}
      data-qa-id={`${qaId}-${index}-sections`}
      renderInputGroup={namePrefix => (
        <>
          <ImagePickerField
            label={`${getText('Image')}`}
            name={`${namePrefix}image`}
            previewWidth={200}
            previewHeight={150}
            previewCrop
            hideRemove={false}
          />
          <RichTextField
            simple
            name={`${namePrefix}content`}
            label={`${getText('Content')}*`}
          />
        </>
      )}
    />
  </Flex>
);

export const CtaPromoBlock = ({
  inputId,
  name,
  'data-qa-id': qaId,
  index
}: {
  inputId: Function,
  name: string,
  index: number,
  hasError: string,
  placeholder: string,
  'data-qa-id': string
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <InputField
      name={`${name}.${index}.data.headline`}
      label={`${getText('Headline')}*`}
      placeholder={getText('Headline')}
      data-qa-id={`${qaId}-headline`}
    />
    <RichTextField
      simple
      name={`${name}.${index}.data.text`}
      label={`${getText('Text')}*`}
      placeholder={getText('Text')}
      data-qa-id={`${qaId}-text`}
    />
    <ImagePickerField
      id={inputId}
      data-qa-id={`${qaId}-image`}
      label={`${getText('Image')}*`}
      name={`${name}.${index}.data.image`}
      previewWidth={300}
      previewHeight={200}
      previewCrop
      hideRemove
    />
    <InputField
      name={`${name}.${index}.data.primaryButtonText`}
      label={getText('Primary Button Text')}
      data-qa-id={`${qaId}-primary-button-text`}
    />
    <InputField
      name={`${name}.${index}.data.primaryButtonUrl`}
      label={getText('Primary Button URL')}
      data-qa-id={`${qaId}-primary-button-url`}
    />
    <InputField
      name={`${name}.${index}.data.secondaryButtonText`}
      label={getText('Secondary Button Text')}
      data-qa-id={`${qaId}-secondary-button-text`}
    />
    <InputField
      name={`${name}.${index}.data.secondaryButtonUrl`}
      label={getText('Secondary Button URL')}
      data-qa-id={`${qaId}-secondary-button-url`}
    />
  </Flex>
);

export const FourArticlesHighlightBlock = ({
  name,
  'data-qa-id': qaId,
  index
}: {
  inputId: Function,
  name: string,
  index: number,
  updateItem: Function => void,
  value: { data: FourArticlesHighlightBlockData },
  hasError: string,
  placeholder: string,
  'data-qa-id': string
}) => {
  const [subsites, setSubsites] = useState([]);
  useEffect(() => {
    fetchSubsites().then(setSubsites);
  }, []);
  const makeName = suffix => `${name}.${index}.data.${suffix}`;
  const makeQaId = suffix => `${qaId}-${suffix}`;
  return isEmpty(subsites) ? (
    <LoadingSection />
  ) : (
    <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
      <FormContextConsumer>
        {({ updateValues }) => {
          const clearData = () =>
            updateValues({
              [makeName('pageOneId')]: '',
              [makeName('pageOneTitle')]: '',
              [makeName('pageTwoId')]: '',
              [makeName('pageTwoTitle')]: '',
              [makeName('pageThreeId')]: '',
              [makeName('pageThreeTitle')]: '',
              [makeName('pageFourId')]: '',
              [makeName('pageFourTitle')]: '',
              [makeName('pageOneImage')]: null,
              [makeName('pageTwoImage')]: null,
              [makeName('pageThreeImage')]: null,
              [makeName('pageFourImage')]: null
            });
          return (
            <SelectField
              label={getText('Subsite')}
              name={makeName('subsiteId')}
              data-qa-id={makeQaId('subsite-id')}
              options={subsites.map(({ id, name: subsiteName }) => ({
                value: id,
                label: subsiteName
              }))}
              onUpdateValue={clearData}
            />
          );
        }}
      </FormContextConsumer>
      <InputField
        name={makeName('headline')}
        label={`${getText('Headline')}`}
        placeholder={getText('Headline')}
        data-qa-id={makeQaId('headline')}
      />
      <CheckboxField
        name={makeName('useRecentArticles')}
        label={getText('Use most recent articles')}
        data-qa-id={makeQaId('use-recent-articles')}
      />
      <FormContextConsumer>
        {({ getValue }) => {
          const sid = stringify(getValue(makeName('subsiteId')));
          const filterOptions = `subsiteId: ${sid}`;
          return (
            !getValue(makeName('useRecentArticles')) && (
              <>
                <DualAutocompleteTextInputField
                  displayName={makeName('pageOneTitle')}
                  valueName={makeName('pageOneId')}
                  label={`${getText('Page One')}*`}
                  placeholder={getText('Page One')}
                  data-qa-id={makeQaId('page-one-title')}
                  filterOptions={filterOptions}
                  autocompleteType="CONTENT"
                />
                <ImagePickerField
                  label={`${getText('Page One Image')}`}
                  name={makeName('pageOneImage')}
                  data-qa-id={makeQaId('page-one-image')}
                  previewWidth={150}
                  previewHeight={150}
                  previewCrop
                />
                <DualAutocompleteTextInputField
                  displayName={makeName('pageTwoTitle')}
                  valueName={makeName('pageTwoId')}
                  label={`${getText('Page Two')}*`}
                  placeholder={getText('Page Two')}
                  data-qa-id={makeQaId('page-two-title')}
                  filterOptions={filterOptions}
                  autocompleteType="CONTENT"
                />
                <ImagePickerField
                  label={`${getText('Page Two Image')}`}
                  name={makeName('pageTwoImage')}
                  data-qa-id={makeQaId('page-two-image')}
                  previewWidth={150}
                  previewHeight={150}
                  previewCrop
                />
                <DualAutocompleteTextInputField
                  displayName={makeName('pageThreeTitle')}
                  valueName={makeName('pageThreeId')}
                  label={`${getText('Page Three')}*`}
                  placeholder={getText('Page Three')}
                  data-qa-id={makeQaId('page-three-title')}
                  filterOptions={filterOptions}
                  autocompleteType="CONTENT"
                />
                <ImagePickerField
                  label={`${getText('Page Three Image')}`}
                  name={makeName('pageThreeImage')}
                  data-qa-id={makeQaId('page-three-image')}
                  previewWidth={150}
                  previewHeight={150}
                  previewCrop
                />
                <DualAutocompleteTextInputField
                  displayName={makeName('pageFourTitle')}
                  valueName={makeName('pageFourId')}
                  label={`${getText('Page Four')}*`}
                  placeholder={getText('Page Four')}
                  data-qa-id={makeQaId('page-four-title')}
                  filterOptions={filterOptions}
                  autocompleteType="CONTENT"
                />
                <ImagePickerField
                  label={`${getText('Page Four Image')}`}
                  name={makeName('pageFourImage')}
                  data-qa-id={makeQaId('page-four-image')}
                  previewWidth={150}
                  previewHeight={150}
                  previewCrop
                />
              </>
            )
          );
        }}
      </FormContextConsumer>
      <InputField
        name={makeName('buttonText')}
        label={`${getText('Button Text')}`}
        placeholder={getText('Apply Now')}
        data-qa-id={makeQaId('button-text')}
      />
      <InputField
        name={makeName('buttonUrl')}
        label={`${getText('Button URL')}`}
        placeholder={getText('/example')}
        data-qa-id={makeQaId('button-url')}
      />
    </Flex>
  );
};

export const CategoryHighlightBlock = ({
  name,
  'data-qa-id': qaId,
  index,
  value,
  subsiteId,
  locale
}: {
  inputId: Function,
  name: string,
  index: number,
  value: { data: CategoryHighlightData },
  hasError: string,
  placeholder: string,
  subsiteId: string,
  locale: string,
  'data-qa-id': string
}) => {
  const [autocompleteInputTexts, updateAcInputTexts] = useState({});
  const updateAutocompleteInputText = (text: string, migIndex: number) =>
    updateAcInputTexts(
      updateObject(autocompleteInputTexts, { [migIndex]: text })
    );
  const [pageIdToLabelMap, setPageIdToLabelMap] = useState(
    value.data.pages
      ? arrToObject(value.data.pages.map(p => [p.id, p.title]))
      : {}
  );

  return (
    // not sure why, but this this instance of categoriescontainer seems to require categories as an OwnProp. Other usages do not.
    // $FlowFixMe: see comment above
    <CategoriesContainer
      locale={locale}
      subsiteId={subsiteId}
      render={({ categories }) => (
        <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
          <InputField
            name={`${name}.${index}.data.headline`}
            label={`${getText('Headline')}`}
            data-qa-id={`${qaId}-headline`}
          />
          <Divider dashed my="48px" />
          <MultiInputGroup
            title={getText('Categories')}
            name={`${name}.${index}.data.categories`}
            minInputGroups={1}
            data-qa-id={`${qaId}-${index}-categories`}
            pageIdToLabelMap={pageIdToLabelMap}
            autocompleteInputTexts={autocompleteInputTexts}
            updateAutocompleteInputText={updateAutocompleteInputText}
            categories={categories}
            renderInputGroup={(
              namePrefix,
              category,
              updateSection,
              migIndex
            ) => {
              const categoryOptions = categories.map(c => ({
                value: c.id,
                label: c.name,
                slug: c.slug
              }));
              const categoryMap = arrToObject(categories.map(c => [c.id, c]));
              const currentCategory = categoryMap[category.id];
              const categorySlug = currentCategory ? currentCategory.slug : '';
              const categoryId = currentCategory ? currentCategory.id : '';
              const filterOptions = `subsiteId: ${subsiteId} AND locale: ${locale} AND categories:${categorySlug}`;
              const pageIds = category.pageIds || [];
              return (
                <>
                  <Field>
                    <SearchableSelect
                      name={`${namePrefix}id`}
                      options={categoryOptions}
                      value={categoryId}
                      searchable
                      placeholder={getText('Select a category')}
                      onChange={e => {
                        updateSection(c => updateObject(c, { id: e }));
                      }}
                      className="qa-id-select-category"
                      py={0}
                    />
                  </Field>
                  <ImagePickerField
                    label={`${getText('Image')}*`}
                    name={`${namePrefix}image`}
                    previewWidth={200}
                    previewHeight={150}
                    previewCrop
                    hideRemove={false}
                  />
                  <InputField
                    name={`${namePrefix}description`}
                    label={`${getText('Description')}*`}
                  />
                  <MultiPillField
                    name={`${namePrefix}pageIds`}
                    label={`${getText('Pages or Posts')}*`}
                    emptyMessage={getText('No pages or posts selected')}
                    value={pageIds}
                    idToLabelMap={pageIdToLabelMap}
                    onDelete={deleteIndex => {
                      updateSection(c =>
                        updateObject(c, {
                          pageIds: removeArrayIndex(
                            c.pageIds || [],
                            deleteIndex
                          )
                        })
                      );
                    }}
                  />
                  <AutocompleteTextInputField
                    name={`${namePrefix}pages-autocomplete`}
                    label={`${getText('Add Page or Post (up to 3)')}`}
                    value={autocompleteInputTexts[migIndex] || ''}
                    disabled={pageIds.length >= 3}
                    filterOptions={filterOptions}
                    onSuggestionSelected={(e, payload) => {
                      const { value: pageId } = payload.suggestion;
                      updateSection(c =>
                        updateObject(c, {
                          pageIds: (c.pageIds || []).concat([pageId])
                        })
                      );

                      setPageIdToLabelMap({
                        ...pageIdToLabelMap,
                        [pageId]: payload.suggestion.name
                      });
                      updateAutocompleteInputText('', migIndex);
                    }}
                    onChange={e => {
                      updateAutocompleteInputText(e.target.value, migIndex);
                    }}
                    autocompleteType="CONTENT"
                  />
                </>
              );
            }}
          />
        </Flex>
      )}
    />
  );
};

export const RandomGradProgramsBlock = ({
  name,
  'data-qa-id': qaId,
  index
}: {
  inputId: Function,
  name: string,
  index: number,
  placeholder: string,
  'data-qa-id': string
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <InputField
      name={`${name}.${index}.data.headline`}
      label={`${getText('Headline')}`}
      data-qa-id={`${qaId}-headline`}
    />
  </Flex>
);

export const RecommendedActionsBlock = ({
  name,
  'data-qa-id': qaId,
  index
}: {
  inputId: Function,
  name: string,
  index: number,
  placeholder: string,
  'data-qa-id': string
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <InputField
      name={`${name}.${index}.data.headline`}
      label={`${getText('Headline')}`}
      data-qa-id={`${qaId}-headline`}
    />
  </Flex>
);

export const IdealistDaysMapBlock = ({
  name,
  'data-qa-id': qaId,
  index
}: {
  inputId: Function,
  name: string,
  index: number,
  hasError: string,
  placeholder: string,
  'data-qa-id': string
}) => {
  const socialLinkPrefix = `${name}.${index}.data.socialShareLink`;

  return (
    <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
      <RadioButtonsField
        name={`${name}.${index}.data.mapType`}
        label={`${getText('Map Type')}`}
        data-qa-id={`${qaId}-map-type`}
        buttons={[
          { label: getText('Idealist Days Map'), value: 'DAYS' },
          { label: getText('Groups Map'), value: 'GROUPS' }
        ]}
      />
      <FormContextConsumer>
        {({ getValue }) => {
          const mapType = getValue(`${name}.${index}.data.mapType`);
          return (
            <>
              {mapType === 'DAYS' && (
                <RadioButtonsField
                  name={`${name}.${index}.data.mapId`}
                  label={`${getText('Date')}*`}
                  data-qa-id={`${qaId}-date`}
                  buttons={IDEALIST_DAY_EVENT_DATES.map(
                    ({ id, name: label }) => ({
                      label,
                      value: id
                    })
                  )}
                />
              )}
              {mapType === 'GROUPS' && (
                <InputField
                  label={`${getText('Map Name')}*`}
                  name={`${name}.${index}.data.mapId`}
                />
              )}
            </>
          );
        }}
      </FormContextConsumer>
      <RichTextField
        simple
        name={`${name}.${index}.data.instructions`}
        label={`${getText('Overlay Message')}*`}
      />
      <InputField
        name={`${name}.${index}.data.buttonText`}
        label={`${getText('Overlay Button Text')}`}
      />
      <SelectField
        name={`${name}.${index}.data.defaultFullscreen`}
        data-qa-id={`${qaId}-defaultFullscreen`}
        label={getText('Default Size')}
        description={getText(
          'Whether it is fullscreen or in page by default when loading the page'
        )}
        options={[
          { value: 'false', label: 'In Page' },
          { value: 'true', label: 'Fullscreen' }
        ]}
      />
      <InputField
        description="Appears on modal"
        label={`${getText('Prompt')}`}
        name={`${name}.${index}.data.prompt`}
      />

      <Divider light dashed />

      <HeadingSmall mb="8px">Sharing (Optional)</HeadingSmall>
      <RadioButtonsField
        name={`${socialLinkPrefix}.type`}
        label={getText('Social Share Link')}
        buttons={[
          { value: 'URL', label: 'URL' },
          { value: 'CMS_PAGE', label: 'CMS Page' }
        ]}
      />
      <FormContextConsumer>
        {({ getValue }) =>
          getValue(`${socialLinkPrefix}.type`) === 'CMS_PAGE' ? (
            <DualAutocompleteTextInputField
              displayName={`${socialLinkPrefix}.data.page.title`}
              valueName={`${socialLinkPrefix}.data.pageId`}
              description={getText(
                'Leave empty to share current page. To share Idealist pages, enter the leading slash. E.g., /en/jobs'
              )}
              placeholder={getText('Page')}
              autocompleteType="CONTENT"
            />
          ) : (
            <InputField
              description={getText(
                'Leave empty to share current page. To share Idealist pages, enter the leading slash. E.g., /en/jobs'
              )}
              name={`${socialLinkPrefix}.data.url`}
            />
          )
        }
      </FormContextConsumer>
      <InputField
        label={`${getText('Social Share Message')}`}
        description={getText(
          'Enter text that should be displayed when users share this map to Facebook, Twitter, or email'
        )}
        name={`${name}.${index}.data.socialShareMessage`}
      />
    </Flex>
  );
};

export const SplitContentBlock = ({
  name,
  'data-qa-id': qaId,
  index
}: {
  name: string,
  'data-qa-id': string,
  index: number
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <SelectField
      name={`${name}.${index}.data.appearance`}
      label={getText('Appearance')}
      description={getText(
        'Appearance will be side-by-side unless one section is of type Image or Video and the other is Text or Blockquote.'
      )}
      options={[
        { value: 'SIDE_BY_SIDE', label: 'Side-by-side Content' },
        { value: 'OVERLAPPING', label: 'Overlapping Content' },
        { value: 'FULL_WIDTH', label: 'Full-width Content' }
      ]}
    />
    <MultiInputGroup
      title={getText('Split Content')}
      inputGroupCount={2}
      name={`${name}.${index}.data.sections`}
      minInputGroups={2}
      maxInputGroups={2}
      data-qa-id={`${qaId}-${index}-sections`}
      renderInputGroup={(namePrefix, section) => (
        <>
          <RadioButtonsField
            name={`${namePrefix}type`}
            label={`${getText('Content Type')}`}
            buttons={[
              { value: 'IMAGE', label: 'Image' },
              { value: 'VIDEO', label: 'Video' },
              { value: 'TEXT', label: 'Text' },
              { value: 'BLOCKQUOTE', label: 'Block Quote' }
            ]}
          />
          {(type => {
            switch (type) {
              case 'TEXT':
                return (
                  <>
                    <InputField
                      name={`${namePrefix}data.label`}
                      label={`${getText('Label')}`}
                      description={getText('Small text above headline')}
                    />
                    <RichTextField
                      name={`${namePrefix}data.headline`}
                      label={`${getText('Headline')}`}
                      description={getText('Suggested maximum: 50 characters')}
                      h2
                    />
                    <RichTextField
                      simple
                      name={`${namePrefix}data.html`}
                      label={`${getText('Body')}*`}
                      description={getText('Suggested maximum: 300 characters')}
                    />
                    <ButtonLinkGroup namePrefix={`${namePrefix}data.button.`} />
                  </>
                );
              case 'BLOCKQUOTE':
                return (
                  <>
                    <RichTextField
                      simple
                      name={`${namePrefix}data.quote`}
                      label={`${getText('Quote')}*`}
                    />
                    <InputField
                      name={`${namePrefix}data.cite`}
                      label={`${getText('Citation')}`}
                    />
                  </>
                );
              case 'VIDEO':
                return (
                  <InputField
                    name={`${namePrefix}data.url`}
                    label={`${getText('Video URL')}*`}
                    placeholder={getText(
                      'e.g. https://youtu.be/ktKVQfNQXQw or https://vimeo.com/49671279'
                    )}
                  />
                );
              case 'IMAGE':
              default:
                return (
                  <ImagePickerField
                    name={`${namePrefix}data.image`}
                    label={`${getText('Image')}`}
                    previewWidth={150}
                    previewHeight={200}
                    disableCrop
                  />
                );
            }
          })(section.type)}
        </>
      )}
    />
  </Flex>
);

export const ImageGalleryBlock = ({
  name,
  index,
  'data-qa-id': qaId
}: {
  inputId: Function,
  name: string,
  index: number,
  hasError: string,
  placeholder: string,
  'data-qa-id': string
}) => (
  <GalleryPickerField
    name={`${name}.${index}.data.images`}
    data-qa-id={qaId}
    label={getText('Image Gallery')}
    imageOnly
  />
);

export const SupportContactFormBlock = () => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto" />
);

const GridTile = ({ namePrefix }: { namePrefix: string }) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <InputField name={`${namePrefix}heading`} label={getText('Heading*')} />
    <InputField
      name={`${namePrefix}subHeading`}
      label={getText('Subheading')}
    />
    <RichTextField
      name={`${namePrefix}description`}
      label={getText('Description*')}
    />
    <ImagePickerField
      label={`${getText('Thumbnail*')}`}
      name={`${namePrefix}thumbnail`}
      previewWidth={120}
      previewHeight={120}
    />
    <ImagePickerField
      label={`${getText('Image*')}`}
      name={`${namePrefix}image`}
      description="Recommended minimum size 1280 x 1280 with important content in the center (left and right sides will be cropped on desktop)"
      previewWidth={200}
      previewHeight={150}
    />
  </Flex>
);

const GridSection = ({
  namePrefix,
  numSections
}: {
  namePrefix: string,
  numSections: number
}) => (
  <Flex flexDirection="column" alignContent="stretch" flex="1 1 auto">
    <InputField
      name={`${namePrefix}name`}
      label={`${getText('Section Title')}${numSections > 1 ? '*' : ''}`}
    />
    <Box p={24} borderRadius="8px" bg={colors.fog}>
      <Label>{getText('Tiles')}</Label>
      <FormContextConsumer>
        {({ getValue, errors }) => (
          <MultiInputGroup
            title={getText('Tiles')}
            name={`${namePrefix}tiles`}
            defaultData={[
              {
                heading: '',
                subHeading: null,
                description: '',
                thumbnail: null,
                image: null
              }
            ]}
            minInputGroups={1}
            data-qa-id={`${namePrefix.replace(/\./g, '-')}`}
            addText={
              /* TRANSLATORS: Text to add flash card tile to question in CMS */ getText(
                '+ Add Tile'
              )
            }
            errors={errors}
            getValue={getValue}
            collapsible
            renderInputGroup={tileNamePrefix => (
              <GridTile namePrefix={tileNamePrefix} />
            )}
            renderTitle={(tileNamePrefix, tileSection, tileIndex) => {
              const title = ((getValue(
                `${namePrefix}tiles.${tileIndex}.heading`
              ): any): string);
              return `Tile ${tileIndex + 1}: ${title}`;
            }}
          />
        )}
      </FormContextConsumer>
    </Box>
  </Flex>
);

export const TileGridBlock = ({
  name,
  index,
  qaId
}: {
  name: string,
  index: number,
  qaId: string
}) => (
  <>
    <SelectField
      name={`${name}.${index}.data.imageSize`}
      data-qa-id={`${qaId}-data-image-size`}
      label={getText('Image Size')}
      options={[
        { value: 'LARGE', label: 'Large (Circle Crop)' },
        { value: 'SMALL', label: 'Small' }
      ]}
    />
    <FormContextConsumer>
      {({ getValue, errors }) => (
        <MultiInputGroup
          title={getText('Sections')}
          name={`${name}.${index}.data.sections`}
          minInputGroups={1}
          maxInputGroups={4}
          data-qa-id={`${qaId}-sections`}
          defaultData={[
            {
              name: '',
              tiles: [
                {
                  heading: '',
                  subHeading: null,
                  description: '',
                  thumbnail: null,
                  image: null
                }
              ]
            }
          ]}
          addText={getText('+ Add Section')}
          getValue={getValue}
          renderInputGroup={sectionPrefix => (
            <GridSection
              namePrefix={sectionPrefix}
              numSections={
                ((getValue(
                  `${name}.${index}.data.sections`
                ): any): Array<TileGridSection>).length
              }
            />
          )}
          errors={errors}
        />
      )}
    </FormContextConsumer>
  </>
);

export const ScheduledContentBlock = ({
  name,
  index,
  'data-qa-id': qaId
}: {
  inputId: Function,
  name: string,
  index: number,
  hasError: string,
  placeholder: string,
  'data-qa-id': string
}) => {
  const [scheduledContentQueues, setScheduledContentQueues] = useState([]);
  useEffect(() => {
    fetchScheduledContentQueues().then(setScheduledContentQueues);
  }, []);
  return (
    <SearchableSelectField
      data-qa-id={`${qaId}-queue-id`}
      name={`${name}.${index}.data.queueId`}
      description={getText(
        'Select the queue you want to get scheduled content from'
      )}
      label={getText('Scheduled Content Queue')}
      maxSelected={1}
      options={[{ value: '', label: 'Select a Queue' }].concat(
        scheduledContentQueues
          ? scheduledContentQueues.map(({ id, name: queueName }) => ({
              label: queueName,
              value: id
            }))
          : []
      )}
    />
  );
};

export const DonateBlock = ({
  name,
  index,
  'data-qa-id': qaId
}: {
  name: string,
  index: number,
  'data-qa-id': string
}) => (
  <>
    <InputField
      name={`${name}.${index}.data.campaignId`}
      label={`${getText('Campaign ID')}`}
      description={getText(
        `After you’ve created a campaign in Salesforce, paste that ID here`
      )}
    />
    <ImagePickerField
      data-qa-id={`${qaId}-bg-image`}
      label={`${getText('Background Image')}*`}
      name={`${name}.${index}.data.bgImage`}
      previewWidth={480}
      previewHeight={360}
      hideRemove
    />
    <RichTextField
      h1
      label={`${getText('Headline')}`}
      name={`${name}.${index}.data.headline`}
      description={getText('Suggested maximum: 50 characters')}
    />
    <RichTextField
      simple
      label={`${getText('Body')}`}
      name={`${name}.${index}.data.body`}
      description={getText('Suggested maximum: 300 characters')}
    />
    <RichTextField
      h1
      label={`${getText('Thank You Headline')}`}
      name={`${name}.${index}.data.thankYouHeadline`}
      description={getText(
        'Displayed after a successful donation. Suggested maximum: 50 characters'
      )}
    />
    <RichTextField
      simple
      label={`${getText('Thank You Body')}`}
      name={`${name}.${index}.data.thankYouBody`}
      description={getText(
        'Displayed after a successful donation. Suggested maximum: 300 characters'
      )}
    />
  </>
);

const EMPTY_OPTION = {
  value: '',
  label: getText('Select One'),
  disabled: true
};

const onUpdateFairId = ({
  fairId,
  getValue,
  updateValue,
  name,
  index,
  setLocationOptions,
  getFair
}: {
  fairId: mixed,
  getValue: Function,
  updateValue: Function,
  name: string,
  index: number,
  setLocationOptions: Function,
  getFair: Function
}) => {
  const fair = getFair(fairId);
  const locationId = getValue(`${name}.${index}.data.locationId`);
  if (
    typeof fairId === 'string' &&
    fair &&
    !fair.some(option => option.value === locationId)
  ) {
    updateValue(`${name}.${index}.data.locationId`, null);
  }
  setLocationOptions(fair);
};

export const GradFairGradProgramLinksBlock = ({
  name,
  index,
  value: { data }
}: {
  name: string,
  index: number,
  value: { data: { fairId: ?string, locationId: ?string } }
}) => {
  const [gradFairsById, setGradFairsById] = useState({});
  const [locationOptions, setLocationOptions] = useState([]);
  const [initialFairId] = useState(data.fairId);

  const getFair = useCallback(
    fairId => {
      const fair =
        gradFairsById && typeof fairId === 'string'
          ? gradFairsById[fairId] || null
          : null;
      return [EMPTY_OPTION].concat(
        fair
          ? Object.keys(fair).map(city => ({
              value: city,
              label: fair[city].name
            }))
          : []
      );
    },
    [gradFairsById]
  );

  useEffect(() => {
    fetchGradFairs().then(fairs => {
      setGradFairsById(fairs);
    });
  }, []);

  useEffect(
    () => {
      const fair = getFair(initialFairId);
      setLocationOptions(fair);
    },
    [initialFairId, gradFairsById, getFair]
  );

  const fairIds = useMemo(() => Object.keys(gradFairsById), [gradFairsById]);

  return fairIds.length > 0 ? (
    <FormContextConsumer>
      {({ getValue, updateValue }) => (
        <>
          <SelectField
            name={`${name}.${index}.data.fairId`}
            label={`${getText('Fair')}`}
            options={[EMPTY_OPTION].concat(
              fairIds.map(fairId => ({
                label: fairId,
                value: fairId
              }))
            )}
            onUpdateValue={value =>
              onUpdateFairId({
                fairId: value,
                getValue,
                updateValue,
                name,
                index,
                getFair,
                setLocationOptions
              })
            }
            key={data.locationId}
          />
          <SelectField
            name={`${name}.${index}.data.locationId`}
            label={`${getText('Location')}`}
            options={locationOptions}
          />
        </>
      )}
    </FormContextConsumer>
  ) : (
    <Box
      width={50}
      height={50}
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <Spinner />
    </Box>
  );
};
